import React from 'react';
import FileService from 'services/FileService';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { useMaterialPropertiesLookup, useUnCpcCodes } from 'services/api/queries';
import { EPDProductInformationModel, ProductStatusEnum } from 'types/types';
import { PdfGridsArea, PdfH1, PdfPage, PdfTable, PdfThLabel } from './Styles.styled';
import { formatByLines } from './formatters';
import { EPDProductClassificationId } from 'components/v2/compiler/constants';
import styled from 'styled-components';
import usePcrV2 from 'components/v2/compiler/hooks/usePcrV2';
import usePdfData from './utils/usePdfData';
import { getOptionsFromDictionary } from 'util/utils';
import { PDFImagesViewFullWidth } from 'components/v2/images-upload/PDFImagesViewFullWidth';
import { getDisplayRegionName } from './utils/getDisplayRegionName';

const ProductInformationPage: React.FunctionComponent<{
  productInformation: EPDProductInformationModel;
  shouldShowProductsStatusesSection: boolean;
}> = ({ productInformation, shouldShowProductsStatusesSection }) => {
  const isPcrV2 = usePcrV2();
  const { epdDefinition, products, countries, dictionaryValues, regions } = usePdfData();

  const unitTypeOptions = getOptionsFromDictionary(dictionaryValues, EPD_DICTIONARY_CODES.UNIT_TYPE);
  const materialPropertiesLookup = useMaterialPropertiesLookup().data;
  const productClassificationOptions = getOptionsFromDictionary(dictionaryValues, EPD_DICTIONARY_CODES.CLASSIFICATION_CODE);
  const cpcCodes = useUnCpcCodes({ smartFilter: undefined, cpcLevel: undefined, codes: undefined }).data;
  const selectedCpc = cpcCodes?.find((cpc: any) => cpc?.id === productInformation?.uncpcId)?.description;
  const selectedUnitTypeOriginal = !isNaN(Number(productInformation?.unitType))
    ? unitTypeOptions.find((x: any) => x.value === productInformation?.unitType?.toString())
    : unitTypeOptions.find((x: any) => x.label === productInformation?.unitType);
  const showStatusForSingleProduct = epdDefinition.epdClassification === EPDProductClassificationId.SINGLE_PRODUCT_ID;
  const statusMessagesForSingleProduct: Record<ProductStatusEnum, string | null> = {
    [ProductStatusEnum.ForthcomingOrNotYetOnTheMarket]: 'Product not yet on the market.',
    [ProductStatusEnum.RecentlyOnTheMarket]: 'Product recently on the market.',
    [ProductStatusEnum.OnTheMarket]: null,
  };

  const statusForSingleProduct = productInformation.productStatus
    ? statusMessagesForSingleProduct[productInformation.productStatus]
    : '';

  const notYetOnTheMarketProducts = products.filter(
    (p) => p.productStatus === ProductStatusEnum.ForthcomingOrNotYetOnTheMarket
  );

  const notYetOnTheMarketProductsNames = notYetOnTheMarketProducts.map((x) => x.productName).join(', ');

  const recentlyOnTheMarketProducts = products.filter((p) => p.productStatus === ProductStatusEnum.RecentlyOnTheMarket);

  const recentlyOnTheMarketProductsNames = recentlyOnTheMarketProducts.map((x) => x.productName).join(', ');

  const showStatusForProducts = () => {
    const isMultipleProductsNotOrRecentlyOnTheMarket =
      products.filter(
        (p) =>
          p.productStatus === ProductStatusEnum.ForthcomingOrNotYetOnTheMarket ||
          p.productStatus === ProductStatusEnum.RecentlyOnTheMarket
      ).length > 1;

    if (!notYetOnTheMarketProductsNames.trim() && !recentlyOnTheMarketProductsNames.trim()) {
      return null;
    }

    if (showStatusForSingleProduct) {
      return (
        <StyledPdfGridsArea>
          <BoldStatus>{statusForSingleProduct}</BoldStatus>
          <ProductStatusDisclaimerText>
            Results of this EPD shall be used with care as the LCI data for this products are not yet based on 1 year of
            production which may result in increased uncertainty.
          </ProductStatusDisclaimerText>
        </StyledPdfGridsArea>
      );
    }
    return (
      <StyledPdfGridsArea>
        <ProductStatusLabelsArea>
          {notYetOnTheMarketProducts.length > 0 && (
            <BoldStatus>
              {notYetOnTheMarketProductsNames}
              {notYetOnTheMarketProducts.length > 1 ? ' are ' : ' is '}
              not yet on the market.
            </BoldStatus>
          )}
          {recentlyOnTheMarketProducts.length > 0 && (
            <BoldStatus>
              {recentlyOnTheMarketProductsNames}
              {recentlyOnTheMarketProducts.length > 1 ? ' are ' : ' is '}
              recently on the market.
            </BoldStatus>
          )}
        </ProductStatusLabelsArea>
        <ProductStatusDisclaimerText>
          Results of this EPD shall be used with care as the LCI data for
          {isMultipleProductsNotOrRecentlyOnTheMarket ? ' these ' : ' this '}
          products are not yet based on 1 year of production which may result in increased uncertainty.
        </ProductStatusDisclaimerText>
      </StyledPdfGridsArea>
    );
  };

  return (
    <>
      <PdfPage>
        <PdfH1>Product information</PdfH1>
        {shouldShowProductsStatusesSection && isPcrV2 && showStatusForProducts()}
        <PdfTable>
          <thead>
            <tr>
              <PdfThLabel />
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Product name</td>
              <td>{productInformation?.productName}</td>
            </tr>
            <tr>
              <td>Product identification</td>
              <td>{productInformation?.productIdentification}</td>
            </tr>
            <tr>
              <td>Product description</td>
              <td>{productInformation.productDescription}</td>
            </tr>
            {productInformation?.productInformationExternal && (
              <tr>
                <td>Product information from external sources</td>
                <td>{productInformation?.productInformationExternal}</td>
              </tr>
            )}
            <tr>
              <td>Technical purpose of product</td>
              <td>{productInformation?.technicalPurposeOfProduct}</td>
            </tr>
            <tr>
              <td>Manufacturing or service provision description</td>
              <td>{productInformation?.manufacturingOrServiceProvisionDescription}</td>
            </tr>
            {productInformation.productMaterialProperties != null &&
              productInformation.productMaterialProperties.length > 0 && (
                <tr>
                  <td>Material properties</td>
                  <td
                    dangerouslySetInnerHTML={{
                      __html:
                        productInformation?.productMaterialProperties
                          ?.map((item) => {
                            const materialProperty = materialPropertiesLookup?.find(
                              (prop: any) => prop.id === item?.materialPropertyId
                            );

                            if (materialProperty && item?.value !== null) {
                              return `${materialProperty.unitName}: ${item.value} ${materialProperty.unit}`;
                            }

                            return `${materialProperty?.fullName || item?.value || ''}`;
                          })
                          .join('\n') ?? '',
                    }}
                  ></td>
                </tr>
              )}
            {productInformation?.productionSites?.map((productionSite, index) => (
              <tr key={index}>
                <td>
                  {isPcrV2 ? 'Manufacturing site' : 'Production site'} {index > 0 ? index + 1 : ''}
                </td>
                <td>
                  {formatByLines([
                    productionSite.nameOfManufacturer,
                    productionSite.name,
                    productionSite.addressLine,
                    productionSite.addressLineSecond,
                    [productionSite.zipCode, productionSite.city].filter(Boolean).join(' '),
                    getDisplayRegionName(productionSite.regionId, regions),
                    countries.find((country) => country.id === productionSite.countryId)?.name,
                  ])}
                </td>
              </tr>
            ))}
            <tr>
              <td>UN CPC code</td>
              <td>{productInformation?.isUNCPCCodeNotApplicable ? 'No applicable UN CPC code' : selectedCpc}</td>
            </tr>
            {productInformation?.gtin && (
              <tr>
                <td>GTIN</td>
                <td>{productInformation?.gtin}</td>
              </tr>
            )}

            {(productInformation?.productClassifications?.length as number) > 0 && (
              <tr>
                <td>Product classification system</td>
                <td>
                  {productInformation.productClassifications?.map((productClassification, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <br />}
                      {formatByLines([
                        productClassificationOptions.find(
                          (x: any) => x.value == productClassification?.productClassificationType
                        )?.label,
                        productClassification?.description,
                        productClassification?.code,
                      ])}
                    </React.Fragment>
                  ))}
                </td>
              </tr>
            )}
            <tr>
              <td>Geographical scope(s)</td>
              <td>{productInformation.productGeographicalScopes?.map((g) => g.name).join(', ')}</td>
            </tr>
            {productInformation?.geographicalScopeDescription && (
              <tr>
                <td>Geographical scope description</td>
                <td>{productInformation?.geographicalScopeDescription}</td>
              </tr>
            )}
            {(productInformation?.unitValue || selectedUnitTypeOriginal?.label) && (
              <tr>
                <td>Actual or technical lifespan</td>
                <td>
                  {productInformation?.unitValue} {selectedUnitTypeOriginal?.label?.toLowerCase()}
                </td>
              </tr>
            )}
          </tbody>
        </PdfTable>
      </PdfPage>
      <PdfPage>
        <PdfH1>Product images</PdfH1>
        <PDFImagesViewFullWidth images={productInformation.productDetailsImages} />
      </PdfPage>
    </>
  );
};

const BoldStatus = styled.span`
  font-family: 'Inter';
  font-weight: 600;
  font-size: 10pt;
  line-height: 14pt;
  letter-spacing: 0%;
`;

const ProductStatusLabelsArea = styled.div`
  display: flex;
  flex-direction: column;

  &:empty {
    display: none;
  }
`;

const StyledPdfGridsArea = styled(PdfGridsArea)`
  gap: 12pt;
  margin-bottom: 12pt;
`;

const ProductStatusDisclaimerText = styled.div`
  font-family: 'Inter';
  font-weight: 400;
  font-size: 10pt;
  line-height: 14pt;
  letter-spacing: 0%;
`;

export default ProductInformationPage;
